import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingCartAction from "@/components/FloatingCartAction";
import { api } from "@/lib/api";
import { buildLoginPath } from "@/lib/loginRedirect";
import { decimalInputProps, decimalOnly, digitsOnly, integerInputProps, integerOnly, pincodeInputProps } from "@/lib/numberInput";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { ArrowLeft, ShoppingCart } from "lucide-react";


const STRUCTURAL_CATEGORIES = [
  {
    id: "ms-round",
    name: "MS Round",
    image: "/assets/structural_steel_categories/ms_rounds.png",

    items: [
      ["80 MM MS Round", 4],
      ["14 MM MS Round", 137],
      ["6 MM MS Round", 370],
      ["8 MM MS Round", 421],
      ["10 MM MS Round", 270],
      ["12 MM MS Round", 187],
      ["16 MM MS Round", 105],
      ["18 MM MS Round", 83],
      ["20 MM MS Round", 67],
      ["22 MM MS Round", 55],
      ["25 MM MS Round", 43],
      ["28 MM MS Round", 35],
      ["30 MM MS Round", 30],
      ["32 MM MS Round", 26],
      ["36 MM MS Round", 20],
      ["40 MM MS Round", 16],
      ["42 MM MS Round", 15],
      ["45 MM MS Round", 13],
      ["48 MM MS Round", 11],
      ["50 MM MS Round", 10],
      ["52 MM MS Round", 10],
      ["60 MM MS Round", 7],
      ["63 MM MS Round", 6],
      ["65 MM MS Round", 6],
      ["90 MM MS Round", 3],
    ],
  },
  {
    id: "ms-angles",
    name: "MS Angles",
    image: "/assets/structural_steel_categories/ms_angles.png",
    items: [
      ["MS Angles (ISA) 25X25X3", 148],
      ["MS Angles (ISA) 25X25X4", 114],
      ["MS Angles (ISA) 65X65X5", 33],
      ["MS Angles (ISA) 45X45X5", 49],
      ["MS Angles (ISA) 40X40X4", 68],
      ["MS Angle (ISA) 100X100X10", 11],
      ["MS Angles (ISA) 130X130X8", 10],
      ["MS Angles (ISA) 32X32X3", 125],
      ["MS Angles (ISA) 65X65X8", 21],
      ["MS Angles (ISA) 75X75X10", 15],
      ["MS Angles (ISA) 40X40X3", 90],
      ["MS Angles (ISA)150x150x10", 7],
      ["MS Angles (ISA) 200x200x16", 3],
      ["MS Angles 37X37X3 MM", 98],
      ["MS Angles (ISA) 65X65X10 MM", 16],
      ["MS Angles (ISA) 25X25X5", 92],
      ["MS Angles (ISA) 25X25X6", 79],
      ["MS Angles (ISA) 35X35X4", 79],
      ["MS Angles (ISA) 32X32X4", 87],
      ["MS Angles (ISA) 35X35X5", 64],
      ["MS Angles (ISA) 40X40X5", 616],
      ["MS Angles (ISA) 40X40X6", 47],
      ["MS Angles (ISA) 50X50X5", 44],
      ["MS Angle (ISA) 50X50X6", 39],
      ["65X65X6 MS Angles", 28],
      ["MS Angles (ISA) 75X75X5", 28],
      ["75X75X6 MS Angles", 24],
      ["MS Angle (ISA) 75X75X8", 18],
      ["90X90X6 MS Angles", 20],
      ["90X90X8 MS Angles", 15],
      ["100X100X8 MS Angles", 13],
      ["MS Angles (ISA) 110X110X10", 10],
      ["MS Angles (ISA) 110X110X12", 8],
      ["MS Angles (ISA) 130X130X10", 8],
      ["MS Angles (ISA) 130X130X12", 7],
      ["MS Angles (ISA) 150X150X12", 6],
      ["MS Angles (ISA) 150X150X16", 4],
    ],
  },
  {
    id: "ms-squares",
    name: "MS Squares",
    image: "/assets/structural_steel_categories/ms_squares.png",

    items: [
      ["6mm MS Squares", 588],
      ["18mm MS Squares", 65],
      ["13 Ms Squares", 125],
      ["9 Ms Squares", 261],
      ["11 Ms Squares", 175],
      ["8mm MS Squares", 332],
      ["10 mm MS Squares", 212],
      ["12mm MS Squares", 147],
      ["16mm MS Squares", 82],
      ["20mm MS Squares", 53],
      ["25mm MS Squares", 33],
      ["32mm MS Squares", 20],
      ["40 MM MS SQUARES", 13],
      ["50 MM MS SQUARES", 8],

    ],
  },
  {
    id: "ms-channels",
    name: "MS Channels",
    image: "/assets/structural_steel_categories/ms_channels.png",

    items: [
      ["ISMC 150", 10],
      ["100 MS Channels", 17],
      ["ISMC 125", 12],
      ["ISMC 75", 10],
      ["ISMC 150", 8],
      ["ISMC 200", 4],
      ["300 MS Channels", 3],
      ["400 MS Channels", 23],
      ["75 MS CHANNEL", 5],
      ["250 MS Channels", 8],
      ["ISMC 100", 17],
    ],
  },
  {
    id: "ms-plates",
    name: "MS Plates",
    image: "/assets/structural_steel_categories/ms_plates.png",

    items: [
      ["MS Plates 6mmx1250x6300", 2],
      ["MS Plates 6mmx1500x6300", 2],
      ["MS Plates 6mmx1500x12000", 1],
      ["MS Plates 8mmx1250x6300", 1],
      ["MS Plates 8mmx1500x6300", 2],
      ["MS Plates 8mmx1250x12000", 1],
      ["MS Plates 8mmx1500x12000", 1],
      ["MS Plates 10mmx1250x6300", 1],
      ["MS Plates 10mmx1500x12000", 1],
      ["MS Plates 10mmx1250x12000", 1],
      ["MS Plates 12mmx1500x12000", 1],
      ["MS Plates 12mmx1250x12000", 1],
      ["MS Plates12mmx1500x6300", 1],
      ["MS Plates 12mmx1250x6300", 1],
      ["MS Plates 10mmx2000x6300", 1],
      ["MS Plates 3mm", 10],
      ["4MM MS PLATE", 4],
      ["5MM MS PLATE", 3],
      ["6MM MS PLATE", 3],
      ["8 MM MS PLATE", 2],
      ["10MM MS PLATE", 2],
      ["12 MM MS PLATE", 1],
      ["14 MM MS PLATE", 1],
      ["16 MM MS PLATES", 1],
      ["18MM MS Plates", 1],
      ["20 MM MS PLATE", 1],
      ["24MM MS PLATE", 1],
      ["25 MM MS PLATE", 1],
      ["28MM MS PLATE", 1],
      ["32MM MS PLATE", 1],
      ["36MM MS PLATE", 1],
      ["40MM MS PLATE", 1],
      ["50MM MS PLATE", 1],
      ["MS CHEQUERED PLATE 4 MM", 4],
      ["MS CHEQUERED PLATE 1250X6300X5MM", 3],
      ["MS CHEQUERED PLATE 1250X6300 6 MM", 3],
 
     
    ],
  },
  {
    id: "ms-flats",
    name: "MS Flats",
    image: "/assets/structural_steel_categories/ms-flats.png",
    items: [
      ["18 X 4 MS Flats", 294],
      ["20X5 MS Flats", 212],
      ["18 X 4 MS Flats", 283],
      ["25X3 G.1. Flats", 212],
      ["25X4 MS Flats", 169],
      ["25X5 MS FLATS", 141],
      ["25X6 MS FLATS", 106],
      ["25X8 MS Flats", 94],
      ["25X9MS Flats", 132],
      ["32X5 MS Flats", 110],
      ["32X6 MS Flats", 294],
      ["32X8 MS Flats", 82],
      ["32X10MS Flats", 66],
      ["20X5 MS Flats", 55],
      ["40 X 3 MS Flats", 176],
      ["MS Flats 40X5", 106],
      ["40X6 MS Flats", 88],
      ["MS Flats 40X8", 66],
      ["MS Flats 40X10", 53],
      ["40X16MS Flats", 33],
      ["40X25MS Flats", 21],
      ["50X5 MS Flats", 84],
      ["50X6 MS Flats", 70],
      ["50X8 MS Flats", 53],
      ["50X10 MS Flats", 42],
      ["50X12 MS Flats", 35],
      ["50X16 MS Flats", 26],
      ["50X20 MS Flats", 21],
      ["50X25MS Flats", 10],
      ["60X25MS Flats", 84],
      ["65X6 MS Flats", 54],
      ["65X8 MS Flats", 40],
      ["65X10 MS Flats", 32],
      ["65X12 MS Flats", 27],
      ["65X16 MS Flats", 20],
      ["65X20 MS Flats", 16],
      ["75X6 MS Flats", 47],
      ["75X8 MS Flats", 35],
      ["75X10 MS Flats", 28],
      ["75X12 MS Flats", 23],
      ["75X16 MS Flats", 17],
      ["75X20MS Flats", 68],
      ["75X32MS Flat", 53],
      ["80X8 MS Flats", 33],
      ["100X6 MS Flats", 34],
      ["100X8 MS FLATS", 26],
      ["100X10 MS Flats", 21],
      ["100X12MS Flats", 17],
      ["100X16MS Flats", 13],
      ["100X20MS Flats", 10],
      ["100X25MS Flats", 8],
      ["125X6MS Flats", 28],
      ["125X8MS Flats", 21],
      ["125X10 MS Flats", 16],
      ["125X12MS Flats", 14],
      ["125X16MS Flats", 10],
      ["125X20MS Flats", 8],
      ["125X25MS Flats", 6],
      ["150X6MS Flats", 23],
      ["150X8MS Flats", 17],
      ["150X10MS Flats", 14],
      ["150X12 MS Flats", 11],
      ["150X16MS Flats", 8],
      ["150X20MS Flats", 7],
      ["150X25MS Flats", 5],
      ["200X6MS Flats", 17],
      ["200X8MS Flats", 13],
      ["200X10MS Flats", 10],
      ["200X12 MS FLATS", 8],
      ["200X16MS Flats", 6],
    ],
  },
  {
    id: "ms-square-pipes",
    name: "MS Square Pipes",
    image: "/assets/structural_steel_categories/ms-square-pipes.png",
    items: [
      ["MS SQUARE PIPE 32 X 32 X 3.2 MM", 255],
      ["MS SQUARE PIPE 38 X 38 X 3.5 MM", 45],
      ["MS SQUARE PIPE 32 X 32 X 3.2 MM", 255],
      ["MS SQUARE PIPE 38 X 38 X 3.5 MM", 45],
      ["MS SQUARE PIPE 40X 40 X 4 MM", 38],
      ["MS SQUARE PIPE 19 X 19 X 1.6 MM", 198],
      ["MS SQUARE PIPE 80x80x2.9MM", 24],
      ["MS SQUARE PIPE 19 X 19 X 2 MM", 164],
      ["MS SQUARE PIPE 30 X 30 X 2.3 MM", 86],
      ["MS SQUARE PIPE 32 X 32 X 2 MM", 97],
      ["MS SQUARE PIPE 38 X 38 X 2.5 MM", 61],
      ["MS SQUARE PIPE 38 X 38 X 2 MM", 75],
      ["MS SQUARE PIPE 38 X 38 X 1.6 MM", 92],
      ["MS SQUARE PIPE 38 X 38 X 3.2 MM", 49],
      ["MS SQUARE PIPE 38 X 38 X 1.2 MM", 121],
      ["MS SQUARE PIPE 25 X 25 X 1.2 MM", 190],
      ["MS SQUARE PIPE 25 X 25 X 1.6 MM", 143],
      ["MS SQUARE PIPE 25 X 25 X 2 MM", 119],
      ["MS SQUARE PIPE 50X 50 X 2 MM", 57],
      ["MS SQUARE PIPE 50X 50 X 1.2 MM", 91],
      ["MS SQUARE PIPE 50X 50 X 2.5 MM", 45],
      ["MS SQUARE PIPE 50X 50 X 2.9 MM", 39],
      ["MS SQUARE PIPE 100X 100 X 2.9 MM", 19],
      ["MS SQUARE PIPE 72X 72 X 2 MM", 30],
      ["MS SQUARE PIPE 72X 72 X 2.5 MM", 31],
      ["MS SQUARE PIPE 60X 60 X 4 MM", 24],
      ["MS SQUARE PIPE 60X 60 X 2.5 MM", 37],
      ["MS SQUARE PIPE 50X 50 X 1.6 MM", 69],
      ["MS SQUARE PIPE 72X 72 X 1.6 MM", 47],
      ["MS SQUARE PIPE 25 X 25 X 2.5 MM", 99],
      ["MS SQUARE PIPE 50X 50 X 5 MM", 24],
      ["MS SQUARE PIPE 150X 150 X 4 MM", 9],
      ["MS SQUARE PIPE 115X 115 X 4 MM", 12],
      ["MS SQUARE PIPE 30 X 30 X 1.6 MM", 119],
      ["MS SQUARE PIPE 80X 80 X 5 MM", 14],
      ["MS SQUARE PIPE 80X 80 X 2.5 MM", 27],
      ["MS SQUARE PIPE 75X75 X5 MM", 15],
      ["MS SQUARE PIPE 80X 80 X 3.2 MM", 21],
      ["MS SQUARE PIPE 50X 50 X 3.2 MM", 28],
      ["MS SQUARE PIPE 100X 100 X 2 MM", 11],
      ["MS SQUARE PIPE 100X 100 X 4 MM", 26],
      ["MS SQUARE PIPE 60X 60 X 3.6 MM", 29],
      ["MS SQUARE PIPE 50X 50 X 4 MM", 7],
      ["MS SQUARE PIPE 150X 150 X 5 MM", 45],
      ["MS SQUARE PIPE 75X 75 X 2.5 MM", 29],
      ["MS SQUARE PIPE 40X 40 X 2.5 MM", 58],
      ["MS SQUARE PIPE 40X 40 X 3 MM", 49],
      ["MS SQUARE PIPE 40X 40 X 1.6 MM", 88],
      ["MS SQUARE PIPE 72X 72 X 2.9 MM", 26],
      ["MS SQUARE PIPE 20 X 20 X 2 MM", 154],
      ["MS SQUARE PIPE 25 X 25 X 3MM", 85],
      ["MS SQUARE PIPE 20 X 20 X 1.2 MM", 242],
      ["MS SQUARE PIPE 20 X 20 X 1.5 MM", 198],
      ["MS SQUARE PIPE 20 X 20 X 1.9 MM", 161],
      ["MS SQUARE PIPE 20 X 20 X 2.2 MM", 143],
      ["MS SQUARE PIPE 20 X 20 X 2.5 MM", 129],
      ["MS SQUARE PIPE 25 X 25 X 1.5 MM", 154],
      ["MS SQUARE PIPE 25 X 25 X 1.9 MM", 125],
      ["MS SQUARE PIPE 25 X 25 X 2.2 MM", 110],
      ["MS SQUARE PIPE 25 X 25 X 2.8 MM", 90],
      ["MS SQUARE PIPE 38 X 38 X 1.5MM", 90],
      ["MS SQUARE PIPE 38 X 38 X 1.9MM", 79],
      ["MS SQUARE PIPE 38 X 38 X 2.2MM", 69],
      ["MS SQUARE PIPE 38 X 38 X 2.8MM", 51],
      ["MS SQUARE PIPE 38 X 38 X 3.6MM", 44],
      ["MS SQUARE PIPE 38 X 38 X 4MM", 41],
      ["MS SQUARE PIPE 50X 50 X 1.5 MM", 73],
      ["MS SQUARE PIPE 50X 50 X 1.9/2 MM", 59],
      ["MS SQUARE PIPE 50X 50 X 2.2MM", 51],
      ["MS SQUARE PIPE 50X 50 X 2.8MM", 41],
      ["MS SQUARE PIPE 50X 50 X 3.6MM", 32],
      ["MS SQUARE PIPE 60X 60 X 1.2MM",75 ],
      ["MS SQUARE PIPE 50 X 50 X 2.9/3MM", 38],
      ["MS SQUARE PIPE 60X 60 X 1.9MM", 48],
      ["MS SQUARE PIPE 60X 60 X 2.2MM", 42],
      ["MS SQUARE PIPE 60X 60 X 2.6MM", 33],
      ["MS SQUARE PIPE 60X 60 X 3.2MM", 29],
      ["MS SQUARE PIPE 72X 72 X 1.2MM", 62],
      ["MS SQUARE PIPE 72X 72 X 1..5MM", 50],
      ["MS SQUARE PIPE 72X 72 X 1.9MM", 40],
      ["MS SQUARE PIPE 72X 72 X 2.2MM", 35],
      ["MS SQUARE PIPE 72X 72 X 4MM", 20],
      ["MS SQUARE PIPE 72X 72 X 2.8MM", 27],
      ["MS SQUARE PIPE 72X 72 X 3.2MM", 24],
      ["MS SQUARE PIPE 72X 72 X 3.6MM", 22],
      ["MS SQUARE PIPE 30 X 30 X 1.2 MM", 156],
      ["MS SQUARE PIPE 30 X 30 X 1.5 MM", 127],
      ["MS SQUARE PIPE 30 X 30 X 2.0 MM", 102],
      ["MS SQUARE PIPE 30 X 30 X 2.2 MM", 89],
      ["MS SQUARE PIPE 30 X 30 X 2.5MM", 80],
      ["MS SQUARE PIPE 30 X 30 X 2.9/3 MM", 72],
      ["MS SQUARE PIPE 40X40X2 MM", 71],
      ["MS SQUARE PIPE 20 X 20 X 1.6 MM", 186],
      ["MS SQUARE PIPE 150X 150 X 2.9 MM", 12],
      ["MS SQUARE PIPE 25 X 25 X 1.8 MM", 132],
      ["MS SQUARE PIPE 25 X 25 X 2.6 MM", 87],
      ["MS SQUARE PIPE 40X 40 X 3.2MM", 44],
      ["MS SQUARE PIPE32X32X2.9 MM", 65],
      ["MS SQUARE PIPE 40X40X2.6MM", 56],
      ["MS SQUARE PIPE 50X 50 X 2.6 MM", 44],
      ["MS SQUARE PIPE 80X 80 X 4 MM", 17],
      ["MS SQUARE PIPE 200X200X8MM", 4],
      ["MS SQUARE PIPE 200X200X5MM", 5],
      ["MS SQUARE PIPE 100X 100 X 5 MM", 14],
      ["MS SQUARE PIPE 60X 60 X 3MM", 31],
      ["MS SQUARE PIPE 100X 100 X 3.6 MM", 18],
      ["MS SQUARE PIPE 72X 72 X 3MM", 26],
      ["MS SQUARE PIPE 150X 150 X 6 MM", 6],
      ["MS SQUARE PIPE 72X 72 X 5MM", 16],
      ["MS SQUARE PIPE 60X 60 X 4.8 MM", 26],
      ["MS SQUARE PIPE 80X 80 X 4.8 MM", 38],
      ["MS SQUARE PIPE 80X 80 X 6 MM", 12],
      ["MS SQUARE PIPE 100 X 100 X 2.6 MM", 21],
      ],
  },
  
  {
    id: "ms-rectangular-pipes",
    name: "MS Rectangular Pipes",
    image: "/assets/structural_steel_categories/ms-rectangle_pipes.png",
    items: [
      ["MS REC PIPE 40 X 20 X 1.6 MM", 119],
      ["MS REC PIPE 40 X 20 X 2.5 MM", 80],
      ["MS REC PIPE 40 X 20 X 2 MM", 97],
      ["MS REC PIPE 50 X 20 X 4 MM", 45],
      ["MS REC PIPE 50 X 25 X 1.2 MM", 123],
      ["MS REC PIPE 50 X 25 X 1.6 MM", 94],
      ["MS REC PIPE 80 X 40 X 1.6 MM", 57],
      ["MS REC PIPE 75 X 25 X 1.2 MM", 91],
      ["MS REC PIPE 100 X 50 X 1.2 MM", 60],
      ["MS REC PIPE 96 X 48 X 2.3 MM", 33],
      ["MS REC PIPE 96 X 48 X 2 MM", 38],
      ["MS REC PIPE 120 X 60 X 2 MM", 30],
      ["MS REC PIPE 80 X 40 X 2.5 MM", 37],
      ["MS REC PIPE 100 X 50 X 1.6 MM", 45],
      ["MS REC PIPE 100 X 60 X 5 MM", 14],
      ["MS REC PIPE 80 X 40 X 2 MM", 46],
      ["MS REC PIPE 96 X 48 X 1.6 MM", 47],
      ["MS REC PIPE 60 X 40 X 2 MM", 56],
      ["MS REC PIPE 60 X 40 X 3.4 MM", 34],
      ["MS REC PIPE 50 X 25 X 2 MM", 76],
      ["MS REC PIPE 60 X 40 X 1.6 MM", 69],
      ["MS REC PIPE 96 X 48 X 2.5 MM", 45],
      ["MS REC PIPE 100 X 60 X 6.3 MM", 11],
      ["MS REC PIPE 75 X 25 X 1.6 MM", 69],
      ["MS REC PIPE 96 X 48 X 2.5 MM", 31],
      ["MS REC PIPE 250 X 150 X 9.6 MM", 2],
      ["MS REC PIPE 50 X 25 X 2.9 MM", 54],
      ["MS REC PIPE 150 X 75 X 2.5 MM", 12],
      ["MS REC PIPE 122 X 61 X 4.8 MM", 12],
      ["MS REC PIPE 120 X 60 X 3.6 MM", 17],
      ["MS REC PIPE 240 X 120 X 6 MM", 6],
      ["MS REC PIPE 100 X 50 X 3.2 MM", 23],
      ["MS REC PIPE 145 X 82 X 5 MM", 10],
      ["MS REC PIPE 100 X 50 X 2 MM", 37],
      ["MS REC PIPE 150 X 100 X 6 MM", 7],
      ["MS REC PIPE 75 X 40 X 4MM", 25],
      ["MS REC PIPE 65 X 35 X 3 MM", 38],
      ["MS REC PIPE 40 X 20 X 1.2 MM", 156],
      ["MS REC PIPE 50 X 25 X 2.8 MM", 56],
      ["MS REC PIPE 40 X 20 X 1.5 MM", 127],
      ["MS REC PIPE 40 X 20 X 1.9 MM", 102],
      ["MS REC PIPE 40 X 20 X 2.2 MM", 89],
      ["MS REC PIPE 40 X 20 X 2.8 MM", 72],
      ["MS REC PIPE 50 X 25 X 1.5 MM", 100],
      ["MS REC PIPE 50 X 25 X 1.9 MM", 80],
      ["MS REC PIPE 50 X 25 X 2.2 MM", 70],
      ["MS REC PIPE 50 X 25 X 2.5 MM", 62],
      ["MS REC PIPE 50 X 25 X 3.2 MM", 50],
      ["MS REC PIPE 50 X 25 X 3.6 MM", 45],
      ["MS REC PIPE 50 X 25 X 4 MM", 41],
      ["MS REC PIPE 75 X 25 X 1.5 MM", 73],
      ["MS REC PIPE 75 X 25 X 1.9 MM", 59],
      ["MS REC PIPE 75 X 25 X 2.2 MM", 51],
      ["MS REC PIPE 75 X 25 X 2.5MM", 45],
      ["MS REC PIPE 75 X 25 X 2.8MM", 41],
      ["MS REC PIPE 75 X 25 X 3.6MM", 32],
      ["MS REC PIPE 75 X 25 X 4MM", 29],
      ["MS REC PIPE 60 X 40 X 1.2 MM", 91],
      ["MS REC PIPE 60 X 40 X 1.5 MM", 73],
      ["MS REC PIPE 60 X 40 X 1.9 MM", 59],
      ["MS REC PIPE 60 X 40 X 2.2 MM", 51],
      ["MS REC PIPE 60 X 40 X 2.8 MM", 41],
      ["MS REC PIPE 60 X 40 X 3.2 MM", 36],
      ["MS REC PIPE 60 X 40 X 3.6 MM", 32],
      ["MS REC PIPE 60 X 40 X 4 MM", 29],
      ["MS REC PIPE 80 X 40 X 1.2 MM", 75],
      ["MS REC PIPE 80 X 40 X 1.5/1.6 MM", 61],
      ["MS REC PIPE 80 X 40 X 1.9 MM", 48],
      ["MS REC PIPE 80 X 40 X 2.2 MM", 42],
      ["MS REC PIPE 80 X 40 X 2.9/3 MM", 33],
      ["MS REC PIPE 80 X 40 X 3.2 MM", 29],
      ["MS REC PIPE 80 X 40 X 3.6 MM", 26],
      ["MS REC PIPE 80 X 40 X 4 MM", 22],
      ["MS REC PIPE 96 X 48 X 1.2 MM", 62],
      ["MS REC PIPE 96 X 48 X 1.5 MM", 50],
      ["MS REC PIPE 96 X 48 X 1.9 MM", 40],
      ["MS REC PIPE 96 X 48 X 2.2 MM", 35],
      ["MS REC PIPE 96 X 48 X 2.9MM", 27],
      ["MS REC PIPE 96 X 48 X 3.2MM", 24],
      ["MS REC PIPE 96 X 48 X 3.6MM", 22],
      ["MS REC PIPE 96 X 48 X 4MM" , 20],
      ["MS REC PIPE 100 X 50 X 2.5MM", 29],
      ["MS REC PIPE122x61x4mm", 119],
      ["MS REC PIPE 150 X 50 X 4 MM", 29],
      ["MS REC PIPE122x61x4mm", 4],
      ["MS REC PIPE 150 X 50 X 4 MM", 14],
      ["MS REC PIPE 100 X 50 X 4 MM", 19],
      ["MS REC PIPE 122x61x2.5MM", 24],
     ["MS REC PIPE 145 X 82 X 4.8 MM", 13],
      ["MS REC PIPE 142 X 82 X 4.8 MM", 10],
      ["MS REC PIPE 50 X 25 X 3 MM", 53],
      ["MS REC PIPE 80 X 40 X 5 MM", 20],
      ["MS REC PIPE 100 X 50 X 5 MM", 15],
      ["MS REC PIPE 300X200X6 MM", 80],
      ["MS REC PIPE 300X200X8 MM", 80],
       ],
  },
  {
    id: "ms-round-pipes",
    name: "MS Round Pipes",
    image: "/assets/structural_steel_categories/ms-round-pipes.png",
    items: [
      ["MS ROUND PIPE OD 76X2 MM", 45],
      ["MS ROUND PIPE OD 32X1.9MM", 118],
      ["MS ROUND PIPE OD 20X2.6MM", 149],
      ["MS ROUND PIPE OD 21.3X2.0MM", 175],
      ["MS ROUND PIPE OD 42.2X3.2MM", 54],
      ["MS ROUND PIPE OD 32X2.2MM", 103],
      ["MS ROUND PIPE OD 33.4X3.2MM", 69],
      ["MS ROUND PIPE OD 26.7X2.5MM", 111],
      ["MS ROUND PIPE OD 32X1.5MM", 147],
      ["MS ROUND PIPE OD 26.7X1.6MM", 168],
      ["MS ROUND PIPE OD 21.3X3.1MM", 119],
      ["MS ROUND PIPE OD 26.7X3.2MM", 89],
      ["MS ROUND PIPE OD 25X1.2MM", 236],
      ["MS ROUND PIPE OD 25X1.9MM", 154],
      ["MS ROUND PIPE OD 25X2.2MM", 134],
      ["MS ROUND PIPE OD 32X1.2MM", 182],
      ["MS ROUND PIPE OD 25X2.5MM", 120],
      ["MS ROUND PIPE OD 32X2.8MM", 82],
      ["MS ROUND PIPE OD 42.2X2.9MM", 59],
      ["MS ROUND PIPE OD 33X4MM", 58],
      ["MS ROUND PIPE OD 32X2.5MM", 91],
      ["MS ROUND PIPE OD 42.2X2MM", 84],
      ["MS ROUND PIPE OD 33.4X2.5MM", 87],
      ["MS ROUND PIPE OD 42.2X2.6MM", 65],
      ["MS ROUND PIPE OD 42.2X1.6MM", 104],
      ["MS ROUND PIPE 125 NB MEDIUM", 24],
      ["MS ROUND PIPE OD 48.3X1.6MM", 90],
      ["MS ROUND PIPE OD 48.3X2.5MM", 59],
      ["MS ROUND PIPE OD 48.3X2MM", 73],
      ["MS ROUND PIPE 2 INCH OD 2.5MM THICK", 46],
      ["MS ROUND PIPE OD 60.3X2MM", 58],
      ["MS ROUND PIPE OD 60.3X3.5MM", 34],
      ["MS ROUND PIPE OD 73X2.5MM", 38],
      ["MS ROUND PIPE OD 73X2MM", 47],
      ["MS ROUND PIPE OD 73X3.2MM", 30],
      ["MS ROUND PIPE OD 48.3X3.2MM", 46],
      ["MS ROUND PIPE OD 76X3.6MM", 25],
      ["MS ROUND PIPE 2.5 INCH OD 2.5MM THICK", 36],
      ["MS ROUND PIPE OD 73X3.5MM", 27],
      ["MS ROUND PIPE OD 88X2.8MM", 28],
      ["MS ROUND PIPE OD 76X3.2MM", 29],
      ["MS ROUND PIPE OD 88X3.2MM", 24],
      ["MS ROUND PIPE 65 NB HEAVY", 23],
      ["MS ROUND PIPE OD 101.6X2MM", 33],
      ["MS ROUND PIPE OD 88X1.2MM", 64],
      ["MS ROUND PIPE OD 76X2.2MM", 41],
      ["MS ROUND PIPE OD 88X1.5MM" , 52],
      ["MS ROUND PIPE OD 76X2.8MM", 32],
      ["MS ROUND PIPE OD 88X1.9MM", 41],
      ["MS ROUND PIPE OD 88X3.6MM", 22],
      ["MS ROUND PIPE OD 76X1.2MM", 75],
      ["MS ROUND PIPE OD 76X1.5MM", 60],
      ["MS ROUND PIPE OD 76X1.9MM", 48],
      ["MS ROUND PIPE OD 88X2.5MM", 32],
      ["MMS ROUND PIPE 80 NB HEAVY", 20],
      ["MS ROUND PIPE OD 88X2.2MM", 35],
      ["MS ROUND PIPE OD 101.6X4.8MM", 14],
      ["MS ROUND PIPE NB 150X4.8MM", 15],
      ["MS ROUND PIPE OD 114.3X2MM", 19],
      ["MS ROUND PIPE OD 200X4.8MM", 24],
      ["MS ROUND PIPE NB 250X8MM", 17],
      ["MS ROUND PIPE OD 26.7X1.2MM", 220],
      ["MS ROUND PIPE OD 60.3X4MM", 30],
      ["MS ROUND PIPE OD 60.3 X 3.6 MM", 33],
      ["MS ROUND PIPE OD 60.3X3.2MM", 37],
      ["MS ROUND PIPE OD 60.3X2.8MM", 41],
      ["MS ROUND PIPE 50 NB MEDIUM", 52],
      ["MS ROUND PIPE 50 NB X1.9/2MM", 60],
      ["MS ROUND PIPE OD 60.3X1.5MM", 76],
      ["MS ROUND PIPE OD 60.3X1.2MM", 95],
      ["MS ROUND PIPE 40 NB HEAVY", 39],
      ["MS ROUND PIPE OD 48.3X3.6MM", 42],
      ["MS ROUND PIPE OD 48.3X2.9MM", 51],
      ["MS ROUND PIPE OD 48.3X2.2MM", 66],
      ["MS ROUND PIPE OD 48.3X1.9MM", 76],
      ["MS ROUND PIPE OD 48.3X1.5MM", 96],
      ["MS ROUND PIPE OD 48.3X1.2MM", 119],
      ["MS ROUND PIPE OD 42.2X2.2MM", 76],
      ["MS ROUND PIPE OD 42.2X1.9MM", 88],
      ["MS ROUND PIPE OD 42.2X1.5MM", 110],
      ["MS ROUND PIPE OD 42.2X1.2MM", 137],
      ["MS ROUND PIPE NB 125 X4.8MM", 9],
      ["MS ROUND PIPE NB 80X4MM", 2],
      ["MS ROUND PIPE OD 114.3X5.4MM", 2],
      ["MS ROUND PIPE OD 50X3.2MM", 31],
      ["MS ROUND PIPE OD 40 X 2.0 MM", 88],
      ["MS ROUND PIPE OD 32 X 2.0 MM", 112],
      ["MS ROUND PIPE OD 25 X 2.0 MM", 147],
      ["MS ROUND PIPE 300NBX5MM", 4],
      ["MS ROUND PIPE 450NBX4MM", 4],
      ["MS ROUND PIPE 450NBX6MM", 2],
      ["MS ROUND PIPE 25NBX3MM", 102],
      ["MS ROUND PIPE NB 200X4.8MM", 20],
      ["MS ROUND PIPE 125NBX3.6MM", 15],
      ["MS ROUND PIPE 100NBX2.9/3MM", 23],
      ["MS ROUND PIPE OD 48.3X2.9MM", 49],
    ],
  },
  {
    id: "joist-ismb",
    name: "JOIST (ISMB)",
    image: "/assets/structural_steel_categories/joist(ismb).png",

    items: [
      ["125 ISMB", 12],
      ["100 ISMB", 18],
      ["150 ISMB", 11],
      ["175 ISMB", 9],
      ["200 ISMB", 6],
      ["250 ISMB", 5],
      ["300 ISMB", 4],
      ["350 ISMB", 3],
      ["400 ISMB", 3],
      ["450 ISMB", 2],
      ["500 ISMB", 2],
      ["600 ISMB", 1],
    ],
  },
  {
    id: "npb",
    name: "Narrow Parallel Flange Beams (NPB)",
    image: "/assets/structural_steel_categories/narrow_parallel_flange_beams(NPB).png",
    items: [
      ["NPB 200", 7],
      ["NPB 250", 6],
      ["NPB 350", 3],
      ["NPB 300", 4],
      ["NPB 400", 3],
      ["NPB 450", 2],
      ["NPB 500", 1],
      ["NPB 600", 2],
    ],
  },
  {
    id: "wpb",
    name: "Wide Parallel Flange Beams (WPB)",
    image: "/assets/structural_steel_categories/wide-parallel-flange-beams_(WPB).png",

    items: [
      ["WPB 300X300X100", 1],
      ["WPB 300X300X117", 1],
    ],
  },
  {
    id: "ub",
    name: "Universal Beams (UB)",
    image: "/assets/structural_steel_categories/universal-beams(ub).png",

    items: [
      ["UB 406X178X60", 2],
      ["UB 305X165X40", 4],
      ["UB 254X146X37", 4],
      ["UB 254X146X31", 5],
      ["UB 203X133X25", 6],
      ["UB 305X165X46", 3],
      ["UB 610X229X113", 1],
      ["UB 533X210X82", 2],
      ["UB 457X191X74", 2],
      ["UB 406X178X67", 2],
      ["UB 406X178X54", 3],
      ["UB 533X210X92", 1],
    ],
  },
  {
    id: "uc",
    name: "Universal Columns (UC)",
    image: "/assets/structural_steel_categories/univrsal_culumns(UC).png",
    items: [
      ["UC 356X368X153", 1],
      ["UC 356X368X177", 1],
      ["UC 152X152X37", 2],
      ["UC 305X305X118", 1],
      ["UC 305X305X96.9", 2],
      ["UC 152x152x2", 7],
      ["UC 152x152x30", 5],
      ["UC 203X203X46", 3],
      ["UC  356X368X129", 1],
      ["UC  305X305X198", 1],
      ["UC  305X305X158", 1],
      ["UC  305X305X137", 1],
      ["UC  254X254X89", 1],
      ["UC  254X254X73", 1],
      ["UC  203X203X86", 1],
      ["UC  203X203X71", 2],
      ["UC  203X203X60", 2],
      ["UC  203X203X52", 3],
    ],
  },
  {
    id: "c-channels",
    name: "C Channels",
    image: "/assets/structural_steel_categories/c-channels.png",
    items: [
      ["ISMC 100", 2],
      ["75 MS CHANNEL", 2],
      ["100 MS Channels", 2],
      ["ISMC 125", 12],
      ["ISMC 150", 10],
      ["ISMC 200", 8],
      ["250 MS Channels", 5],
      ["300 MS Channels", 4],
      ["400 MS Channels", 3],



    ],
  },
  {
    id: "hr-sheets",
    name: "HR Sheets",
    image: "/assets/structural_steel_categories/hr-sheets.png",
    items: [
      ["MS SHEET 1250X2500X1.6 MM", 25],
      ["MS SHEET 1250X2500X 2 MM", 20],
      ["MS SHEET 2.5 MM", 16],
      ["MS SHEET  3  MM", 13],
      ["MS SHEET 1250X2500X 2 MM", 13],

    ],
  },
  {
    id: "cr-sheets",
    name: "CR Sheets",  
    image: "/assets/structural_steel_categories/cr-sheets.png",
    items: [["1.2MM CR SHEETS", 34]],
  },
];


function toRows(category) {
  return category.items.map(([size, pieces], idx) => ({
    id: `${category.id}-${idx}`,
    size,
    piecesPerTonne: pieces,
    piecesInput: "",
    tonnesInput: "",
    brand: "",
    location: "",
    unit: "MT",
    quantity: 1,
    priceMt: 0,
  }));
}

function trimNumber(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return String(parseFloat(n.toFixed(3)));
}

function normalizeBrand(brand) {
  return String(brand || "").trim().toLowerCase();
}

function normalizeSizeKey(size) {
  return String(size || "").trim().toLowerCase().replace(/\s+/g, " ");
}

const STRUCTURAL_GUEST_FLOW_RESUME_KEY = "RAILAXIS_STRUCTURAL_GUEST_FLOW_RESUME";

export default function StructuralSteelDashboard() {
  const { categoryId } = useParams();
  const locationState = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { items, addItem } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const activeCategory = useMemo(
    () => STRUCTURAL_CATEGORIES.find((c) => c.id === categoryId) || null,
    [categoryId]
  );
  const [rows, setRows] = useState([]);
  const [step, setStep] = useState(1);
  const [vendorRows, setVendorRows] = useState([]);
  const [vendorAddedRows, setVendorAddedRows] = useState([]);
  const [vendorDefaultPincode, setVendorDefaultPincode] = useState("");
  const [vendorDefaultPrice, setVendorDefaultPrice] = useState("");
  const [addingByRow, setAddingByRow] = useState({});
  const [savingAddedByRow, setSavingAddedByRow] = useState({});

  const [pincode, setPincode] = useState("");
  const [location, setLocation] = useState("");
  const [locationQuery, setLocationQuery] = useState("");
  const [locationLoading, setLocationLoading] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState("");

  const isVendor = user?.role === "vendor";
  const assignedVendorSlug = String(user?.vendor_product_slug || "").trim();
  const assignedVendorBrand = String(user?.vendor_brand || "").trim();
  const vendorCanAccessStructural = !isVendor || !assignedVendorSlug || assignedVendorSlug === "structural-steel";
  const activeCategorySizeKeys = useMemo(
    () => new Set((activeCategory?.items || []).map(([size]) => normalizeSizeKey(size))),
    [activeCategory]
  );
  const selectedRows = useMemo(
    () => rows.filter((r) => Number(r.piecesInput) > 0 || Number(r.tonnesInput) > 0),
    [rows]
  );
  const selectedSizeKeys = useMemo(
    () => new Set(selectedRows.map((row) => normalizeSizeKey(row.size))),
    [selectedRows]
  );

  useEffect(() => {
    if (authLoading) return;

    if (!vendorCanAccessStructural && isVendor) {
      navigate(`/products/${assignedVendorSlug === "pvc-pipes" ? "di-pipes" : assignedVendorSlug}`, { replace: true });
      return;
    }
    setLoading(true);
    api.get("/products/structural-steel")
      .then(({ data }) => {
        setProduct(data);
      })
      .catch(() => toast.error("Product not found"))
      .finally(() => setLoading(false));
  }, [authLoading, vendorCanAccessStructural, isVendor, assignedVendorSlug, navigate]);

  useEffect(() => {
    if (!activeCategory) return;
    const nextRows = toRows(activeCategory).map((r, idx) => ({
      ...r,
      brand: isVendor ? assignedVendorBrand : (product?.brands?.[idx % (product?.brands?.length || 1)] || ""),
      location: "",
      priceMt: Math.round((product?.base_price || 78000) * (1 + idx * 0.02)),
    }));
    setRows(nextRows);
    setStep(1);
    setPincode("");
    setLocation("");
    setLocationQuery("");
    setSelectedBrand("");
    setVendorDefaultPincode("");
    setVendorDefaultPrice("");
  }, [activeCategory, product, isVendor, assignedVendorBrand]);

  useEffect(() => {
    if (!user || !activeCategory) return;
    const params = new URLSearchParams(locationState.search);
    if (params.get("resume") !== "1") return;
    try {
      const raw = sessionStorage.getItem(STRUCTURAL_GUEST_FLOW_RESUME_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (!data || data.categoryId !== activeCategory.id) return;
      if (Array.isArray(data.rows)) setRows(data.rows);
      setStep(2);
    } catch {}
    sessionStorage.removeItem(STRUCTURAL_GUEST_FLOW_RESUME_KEY);
    navigate(`/products/structural-steel/${activeCategory.id}`, { replace: true });
  }, [user, activeCategory, locationState.search, navigate]);

  useEffect(() => {
    if (isVendor) return;
    if (step !== 2 || !activeCategory) return;
    if (selectedSizeKeys.size === 0) {
      setVendorRows([]);
      return;
    }

    const filterRelevantRows = (data) => (data || [])
      .map((row) => ({ ...row }))
      .filter((row) => {
        const sizeKey = normalizeSizeKey(row.size);
        return activeCategorySizeKeys.has(sizeKey) && selectedSizeKeys.has(sizeKey);
      });

    const loadAllRows = () => api.get("/products/structural-steel/vendor-products")
      .then(({ data }) => {
        setVendorRows(filterRelevantRows(data));
      });

    if (!/^\d{6}$/.test(pincode)) {
      loadAllRows().catch(() => setVendorRows([]));
      return;
    }

    api.get(`/products/structural-steel/vendor-products?location=${encodeURIComponent(pincode)}`)
      .then(({ data }) => {
        const pincodeRows = filterRelevantRows(data);
        if (pincodeRows.length > 0) {
          setVendorRows(pincodeRows);
          return null;
        }
        return loadAllRows();
      })
      .catch(() => {
        loadAllRows().catch(() => setVendorRows([]));
      });
  }, [step, pincode, isVendor, activeCategory, activeCategorySizeKeys, selectedSizeKeys]);

  useEffect(() => {
    if (!isVendor || !activeCategory) {
      setVendorAddedRows([]);
      return;
    }
    api.get("/products/structural-steel/vendor-products")
      .then(({ data }) => {
        const mapped = (data || []).filter((row) => (
          activeCategorySizeKeys.has(normalizeSizeKey(row.size)) &&
          (!assignedVendorBrand || normalizeBrand(row.brand) === normalizeBrand(assignedVendorBrand))
        ));
        setVendorAddedRows(mapped);
      })
      .catch(() => setVendorAddedRows([]));
  }, [isVendor, activeCategory, activeCategorySizeKeys, assignedVendorBrand]);

  useEffect(() => {
    if (isVendor || step !== 2) return;
    if (!/^\d{6}$/.test(pincode)) {
      setLocation("");
      setLocationQuery("");
      return;
    }

    let alive = true;
    setLocationLoading(true);
    fetch(`https://api.postalpincode.in/pincode/${pincode}`)
      .then((res) => res.json())
      .then((data) => {
        if (!alive) return;
        const first = data?.[0];
        const office = first?.PostOffice?.[0];
        if (first?.Status === "Success" && office) {
          const loc = [office.District, office.State].filter(Boolean).join(", ");
          setLocation(loc || office.Name || "");
          setLocationQuery(String(office.District || office.Name || "").trim());
        } else {
          setLocation("");
          setLocationQuery("");
        }
      })
      .catch(() => {
        if (!alive) return;
        setLocation("");
        setLocationQuery("");
      })
      .finally(() => {
        if (alive) setLocationLoading(false);
      });

    return () => { alive = false; };
  }, [pincode, step, isVendor]);

  const offersByBrand = useMemo(() => {
    if (isVendor) return [];
    if (!selectedRows.length) return [];

    const bestVendorBrand = Object.values(vendorRows.reduce((acc, row) => {
      const vendorKey = row.vendor_id || row.vendor_code || row.vendor_company || row.vendor_name || row.id;
      const brand = String(row.brand || "").trim();
      const sizeKey = normalizeSizeKey(row.size);
      if (!vendorKey || !brand) return acc;
      const key = `${vendorKey}::${brand}`;
      if (!acc[key]) acc[key] = { key, brand, rowsBySize: {} };
      const existing = acc[key].rowsBySize[sizeKey];
      if (!existing || Number(row.price) < Number(existing.price)) {
        acc[key].rowsBySize[sizeKey] = row;
      }
      return acc;
    }, {}));

    const evaluated = bestVendorBrand.map((offer) => {
      const lineItems = selectedRows.flatMap((sel) => {
        const found = offer.rowsBySize[normalizeSizeKey(sel.size)];
        if (!found) return [];
        const pieces = Number(sel.piecesInput);
        const ppt = sel.piecesPerTonne;
        const pricePerPiece = found.unit === "Piece" ? Number(found.price) : Number(found.price) / ppt;
        const lineTotal = pieces * pricePerPiece;
        return [{ size: sel.size, pieces, tonnes: pieces / ppt, piecesPerTonne: ppt, lineTotal, row: found }];
      });
      const subTotal = lineItems.reduce((sum, li) => sum + li.lineTotal, 0);
      return { ...offer, lineItems, subTotal };
    }).filter((o) => o.lineItems.length > 0);

    return Object.values(evaluated.reduce((acc, offer) => {
      if (!acc[offer.brand] || offer.subTotal < acc[offer.brand].subTotal) acc[offer.brand] = offer;
      return acc;
    }, {}));
  }, [vendorRows, selectedRows, isVendor]);

  const selectedOffer = offersByBrand.find((o) => o.brand === selectedBrand) || null;

  useEffect(() => {
    if (isVendor) return;
    if (!offersByBrand.length) {
      setSelectedBrand("");
      return;
    }
    if (!selectedBrand || !offersByBrand.some((o) => o.brand === selectedBrand)) {
      setSelectedBrand(offersByBrand[0].brand);
    }
  }, [offersByBrand, selectedBrand, isVendor]);

  const onPiecesChange = (id, value) => {
    const normalized = integerOnly(value);
    setRows((prev) => prev.map((r) => {
      if (r.id !== id) return r;
      if (!normalized) return { ...r, piecesInput: "", tonnesInput: "" };
      const n = Number(normalized);
      if (!Number.isFinite(n) || n < 0) return r;
      return { ...r, piecesInput: normalized, tonnesInput: trimNumber(n / r.piecesPerTonne) };
    }));
  };

  const onTonnesChange = (id, value) => {
    const normalized = decimalOnly(value, 3);
    setRows((prev) => prev.map((r) => {
      if (r.id !== id) return r;
      if (!normalized) return { ...r, piecesInput: "", tonnesInput: "" };
      const n = Number(normalized);
      if (!Number.isFinite(n) || n < 0) return r;
      return { ...r, tonnesInput: normalized, piecesInput: String(Math.round(n * r.piecesPerTonne)) };
    }));
  };

  const updateVendorRow = (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const applyVendorDefaultPincode = (value) => {
    const nextValue = digitsOnly(value, 6);
    setVendorDefaultPincode(nextValue);
    if (!nextValue) return;
    setRows((prev) => prev.map((row) => ({ ...row, location: nextValue })));
  };

  const applyVendorDefaultPrice = (value) => {
    const nextValue = decimalOnly(value, 2);
    setVendorDefaultPrice(nextValue);
    if (!nextValue) return;
    setRows((prev) => prev.map((row) => ({ ...row, priceMt: nextValue })));
  };

  const redirectGuestToLoginAndResume = () => {
    if (user) return false;
    if (!activeCategory) return false;
    sessionStorage.setItem(STRUCTURAL_GUEST_FLOW_RESUME_KEY, JSON.stringify({
      categoryId: activeCategory.id,
      rows,
    }));
    toast.info("Please login to continue");
    navigate(buildLoginPath(`/products/structural-steel/${activeCategory.id}?resume=1`));
    return true;
  };

  const addVendorRow = async (row) => {
    if (Number(row.quantity) < 1) return toast.error("Enter valid stock");
    if (!/^\d{6}$/.test(String(row.location || ""))) return toast.error("Enter valid 6-digit pincode");
    const ppt = row.piecesPerTonne;
    const price = row.unit === "Piece" ? Number(row.priceMt) / ppt : Number(row.priceMt);
    if (!Number.isFinite(price) || price <= 0) return toast.error("Enter valid price");

    setAddingByRow((p) => ({ ...p, [row.id]: true }));
    try {
      const { data } = await api.post("/products/structural-steel/vendor-products", {
        size: row.size,
        brand: assignedVendorBrand || row.brand,
        location: row.location,
        price,
        unit: row.unit,
        quantity: Number(row.quantity),
      });
      if (activeCategorySizeKeys.has(normalizeSizeKey(data.size))) {
        setVendorAddedRows((prev) => [data, ...prev]);
      }
      toast.success("Product row added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add product");
    } finally {
      setAddingByRow((p) => ({ ...p, [row.id]: false }));
    }
  };

  const updateVendorAddedRow = (id, patch) => {
    setVendorAddedRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const saveVendorAddedRow = async (row) => {
    if (!/^\d{6}$/.test(String(row.location || ""))) return toast.error("Enter valid 6-digit pincode");
    const price = Number(row.price);
    const quantity = Number(row.quantity);
    if (!Number.isFinite(price) || price <= 0) return toast.error("Enter valid price");
    if (!Number.isFinite(quantity) || quantity <= 0) return toast.error("Enter valid quantity");

    setSavingAddedByRow((prev) => ({ ...prev, [row.id]: true }));
    try {
      const { data } = await api.patch(`/products/structural-steel/vendor-products/${row.id}`, {
        size: row.size,
        brand: assignedVendorBrand || row.brand,
        location: row.location,
        price,
        unit: row.unit,
        quantity,
      });
      setVendorAddedRows((prev) => prev.map((entry) => (entry.id === row.id ? { ...entry, ...data } : entry)));
      toast.success("Product updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update product");
    } finally {
      setSavingAddedByRow((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  const addSelectedOfferToCart = () => {
    if (!selectedOffer) return;
    selectedOffer.lineItems.forEach((li) => {
      addItem({
        product_slug: "structural-steel",
        product_name: product?.name || "Structural Steel",
        brand: li.row.brand,
        size: li.size,
        location: location || li.row.location || "N/A",
        unit: "Piece",
        price: Number((li.lineTotal / li.pieces).toFixed(2)),
        quantity: li.pieces,
        pieces_per_tonne: li.piecesPerTonne,
        tonnes_quantity: li.tonnes,
        vendor_id: li.row.vendor_user_id || li.row.vendor_id || li.row.vendor_code || null,
        vendor_name: li.row.vendor_name || null,
        vendor_company: li.row.vendor_company || null,
      });
    });
    toast.success("Selected structural steel items added to cart");
    navigate("/orders");
  };

  const continueStructuralSelection = () => {
    if (redirectGuestToLoginAndResume()) return;
    setStep(2);
  };

  const floatingCartAction = (() => {
    if (isVendor || !activeCategory) return null;
    if (step === 1) {
      return {
        disabled: selectedRows.length === 0,
        onClick: continueStructuralSelection,
        testId: "structural-floating-add-to-cart-btn",
      };
    }
    if (step === 2) {
      return {
        disabled: !selectedOffer,
        onClick: addSelectedOfferToCart,
        testId: "structural-offer-floating-add-to-cart-btn",
      };
    }
    return null;
  })();

  const renderViewCartButton = () => (
    <button
      onClick={() => navigate("/orders")}
      className="inline-flex items-center gap-2 bg-[#111827] text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-[#111827]/85"
      data-testid="view-cart-btn"
    >
      <ShoppingCart className="w-4 h-4" />
      View Cart ({items.length})
    </button>
  );

  return (
    <div className={`bg-white min-h-screen ${floatingCartAction ? "pb-28 md:pb-32" : ""}`}>
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-[#4B5563] hover:text-[#C8A96A] mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Categories
        </button>

        {authLoading || loading ? (
          <div className="py-20 text-center text-[#4B5563]">Loading product…</div>
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div>
                <div className="section-label">Structural Steel</div>
                <h1 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl text-[#111827] mt-2 tracking-tight">Structural Steel</h1>
                <p className="text-[#4B5563] mt-3 max-w-xl">Select category, enter Pieces/Tonnes, and get the best brand offers by pincode.</p>
              </div>
              {!isVendor && !floatingCartAction && renderViewCartButton()}
            </div>

            {!activeCategory && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
                {STRUCTURAL_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => navigate(`/products/structural-steel/${cat.id}`)}
                    className="border rounded-lg overflow-hidden text-left border-gray-200 hover:border-blue-600"
                  >
                    <img src={cat.image} alt={cat.name} className="w-full h-48 object-cover" />
                    <div className="p-3 text-sm font-semibold text-[#111827]">{cat.name}</div>
                  </button>
                ))}
              </div>
            )}

            {activeCategory && (
              <div className="mb-6">
                <button
                  onClick={() => navigate("/products/structural-steel")}
                  className="text-sm font-semibold text-[#1D4ED8] hover:underline"
                >
                  Back to Structural Categories
                </button>
              </div>
            )}

            {activeCategory && (isVendor ? (
              <>
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 text-sm font-semibold">{activeCategory.name} - Vendor Rows</div>
                <div className="border-b border-gray-100 bg-gray-50/60 px-4 py-4">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#111827]">Default Pincode</label>
                      <input
                        {...pincodeInputProps}
                        value={vendorDefaultPincode}
                        onChange={(e) => applyVendorDefaultPincode(e.target.value)}
                        placeholder="6-digit pincode"
                        className="w-full bg-white border border-gray-200 rounded-md px-3 py-2.5 text-sm gold-focus"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#111827]">Price / MT</label>
                      <input
                        {...decimalInputProps}
                        value={vendorDefaultPrice}
                        onChange={(e) => applyVendorDefaultPrice(e.target.value)}
                        placeholder="Enter price"
                        className="w-full bg-white border border-gray-200 rounded-md px-3 py-2.5 text-sm gold-focus"
                      />
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-3">Size</th>
                        <th className="text-left px-4 py-3">Brand</th>
                        <th className="text-left px-4 py-3">Pincode</th>
                        <th className="text-left px-4 py-3">Price</th>
                        <th className="text-left px-4 py-3">Stock</th>
                        <th className="text-right px-4 py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.id} className="border-t border-gray-100">
                          <td className="px-4 py-3 font-semibold">{row.size}</td>
                          <td className="px-4 py-3">
                            <input
                              value={assignedVendorBrand || row.brand}
                              readOnly
                              className="w-40 bg-gray-50 border border-gray-200 rounded-sm px-2 py-1.5"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              {...pincodeInputProps}
                              value={row.location}
                              onChange={(e) => updateVendorRow(row.id, { location: digitsOnly(e.target.value, 6) })}
                              placeholder="6-digit pincode"
                              className="w-32 border border-gray-200 rounded-sm px-2 py-1.5"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <input {...decimalInputProps} value={row.priceMt} onChange={(e) => updateVendorRow(row.id, { priceMt: decimalOnly(e.target.value, 2) })} className="w-24 border border-gray-200 rounded-sm px-2 py-1.5" />
                              <select value={row.unit} onChange={(e) => updateVendorRow(row.id, { unit: e.target.value })} className="border border-gray-200 rounded-sm px-2 py-1.5">
                                <option value="MT">MT</option>
                                <option value="Piece">Piece</option>
                              </select>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <input {...integerInputProps} value={row.quantity} onChange={(e) => updateVendorRow(row.id, { quantity: integerOnly(e.target.value) })} className="w-20 border border-gray-200 rounded-sm px-2 py-1.5" />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button onClick={() => addVendorRow(row)} disabled={!!addingByRow[row.id]} className="btn-gold rounded-sm px-4 py-2 text-xs font-semibold disabled:opacity-60">
                              {addingByRow[row.id] ? "Adding..." : "Add Product"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="mt-6 bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 text-sm font-semibold">Products You Added</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-3">Size</th>
                        <th className="text-left px-4 py-3">Brand</th>
                        <th className="text-left px-4 py-3">Pincode</th>
                        <th className="text-left px-4 py-3">Price</th>
                        <th className="text-left px-4 py-3">Quantity</th>
                        <th className="text-right px-4 py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendorAddedRows.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-6 text-[#4B5563]">No products added yet for this category.</td>
                        </tr>
                      ) : vendorAddedRows.map((row) => (
                        <tr key={row.id} className="border-t border-gray-100">
                          <td className="px-4 py-3">{row.size}</td>
                          <td className="px-4 py-3">{assignedVendorBrand || row.brand}</td>
                          <td className="px-4 py-3">
                            <input
                              {...pincodeInputProps}
                              value={row.location || ""}
                              onChange={(e) => updateVendorAddedRow(row.id, { location: digitsOnly(e.target.value, 6) })}
                              placeholder="6-digit pincode"
                              className="w-32 border border-gray-200 rounded-sm px-2 py-1.5"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <input
                                {...decimalInputProps}
                                value={row.price}
                                onChange={(e) => updateVendorAddedRow(row.id, { price: decimalOnly(e.target.value, 2) })}
                                className="w-28 border border-gray-200 rounded-sm px-2 py-1.5"
                              />
                              <select
                                value={row.unit || "MT"}
                                onChange={(e) => updateVendorAddedRow(row.id, { unit: e.target.value })}
                                className="border border-gray-200 rounded-sm px-2 py-1.5"
                              >
                                <option value="MT">MT</option>
                                <option value="Piece">Piece</option>
                              </select>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <input
                              {...integerInputProps}
                              value={row.quantity}
                              onChange={(e) => updateVendorAddedRow(row.id, { quantity: integerOnly(e.target.value) })}
                              className="w-20 border border-gray-200 rounded-sm px-2 py-1.5"
                            />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => saveVendorAddedRow(row)}
                              disabled={!!savingAddedByRow[row.id]}
                              className="btn-gold rounded-sm px-4 py-2 text-xs font-semibold disabled:opacity-60"
                            >
                              {savingAddedByRow[row.id] ? "Saving..." : "Save"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              </>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6">
                {step === 1 ? (
                  <>
                    <h2 className="text-xl md:text-2xl font-bold text-[#111827]">{activeCategory.name}</h2>
                    <p className="text-sm text-[#4B5563] mt-1 mb-5">Enter Pieces or Tonnes for each row.</p>
                    <div className="space-y-3">
                      {rows.map((row) => {
                        const selected = Number(row.piecesInput) > 0 || Number(row.tonnesInput) > 0;
                        return (
                          <div key={row.id} className={`border rounded-lg p-3 md:p-4 ${selected ? "border-blue-500 bg-blue-50/30" : "border-gray-200"}`}>
                            <div className="grid grid-cols-[28px_minmax(0,1fr)] lg:grid-cols-[28px_1fr_180px_36px_180px] items-center gap-2 md:gap-3">
                              <div className={`w-7 h-7 rounded border text-sm flex items-center justify-center font-bold ${selected ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-gray-300 text-transparent"}`}>
                                ✓
                              </div>
                              <div className="font-semibold text-[#111827]">{row.size}</div>
                              <div className="col-span-2 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 lg:contents">
                                <input {...integerInputProps} value={row.piecesInput} onChange={(e) => onPiecesChange(row.id, e.target.value)} placeholder="Enter Pieces" className="w-full min-w-0 border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-sm" />
                                <div className="text-center text-xs sm:text-sm text-[#6B7280] font-semibold">OR</div>
                                <input {...decimalInputProps} value={row.tonnesInput} onChange={(e) => onTonnesChange(row.id, e.target.value)} placeholder="Enter Tonnes" className="w-full min-w-0 border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-sm" />
                              </div>
                            </div>
                            <div className="text-xs text-[#6B7280] mt-2">1 tonne = {row.piecesPerTonne} pieces</div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <>
                    <h2 className="text-xl md:text-2xl font-bold text-[#111827]">Delivery details</h2>
                    <p className="text-sm text-[#4B5563] mt-1 mb-5">Enter pincode to auto-fill location and show brand offers.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-wider font-semibold text-[#111827] mb-1.5">Pincode</label>
                        <input {...pincodeInputProps} value={pincode} onChange={(e) => setPincode(digitsOnly(e.target.value, 6))} placeholder="Enter 6-digit pincode" className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-wider font-semibold text-[#111827] mb-1.5">Location</label>
                        <input type="text" value={locationLoading ? "Fetching location..." : location} readOnly className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2.5 text-sm" />
                      </div>
                    </div>

                    <div className="mt-6 grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-6">
                      <div className="space-y-3">
                        {location && offersByBrand.length === 0 && (
                          <div className="border border-gray-200 rounded-lg p-4 text-sm text-[#4B5563]">No brand offers found for this pincode location yet.</div>
                        )}
                        {offersByBrand.map((offer) => {
                          const selected = selectedBrand === offer.brand;
                          return (
                            <button key={offer.brand} type="button" onClick={() => setSelectedBrand(offer.brand)} className={`w-full text-left border rounded-lg p-4 ${selected ? "border-blue-600 bg-blue-50/30" : "border-gray-200 bg-white"}`}>
                              <div className="flex items-start justify-between gap-3">
                                <div className="font-bold text-lg text-[#111827]">{offer.brand}</div>
                                <div className="text-right">
                                  <div className="font-bold text-xl text-[#111827]">₹{offer.subTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
                                  <div className="text-xs text-[#6B7280]">{selected ? "Selected" : "Select"}</div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      <div className="border border-gray-200 rounded-lg p-4 h-fit">
                        <h3 className="text-xl font-bold text-[#111827]">Best Price Estimator</h3>
                        {!selectedOffer ? (
                          <p className="text-sm text-[#6B7280] mt-3">Select a brand to view estimate details.</p>
                        ) : (
                          <>
                            <div className="text-sm text-[#4B5563] mt-3">Brand</div>
                            <div className="font-semibold text-[#111827]">{selectedOffer.brand}</div>
                            <div className="text-sm text-[#4B5563] mt-1">{location || "N/A"}</div>
                            <div className="mt-4 space-y-2 text-sm">
                              {selectedOffer.lineItems.map((item) => (
                                <div key={item.size} className="flex justify-between gap-2">
                                  <span>{item.size}</span>
                                  <span className="font-semibold">₹{item.lineTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                                </div>
                              ))}
                            </div>
                            <div className="mt-4 pt-3 border-t border-gray-200 flex justify-between items-center">
                              <span className="font-semibold text-[#111827]">Total</span>
                              <span className="text-xl font-bold text-[#111827]">₹{selectedOffer.subTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 flex justify-start">
                      <button onClick={() => setStep(1)} className="bg-white border border-gray-300 text-[#111827] rounded-md px-5 py-2.5 text-sm font-semibold hover:bg-gray-50">Back</button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </>
        )}
      </section>
      <FloatingCartAction
        visible={!!floatingCartAction}
        label="Add to Cart"
        disabled={floatingCartAction?.disabled}
        onClick={floatingCartAction?.onClick}
        testId={floatingCartAction?.testId}
      />
      <Footer />
    </div>
  );
}
